/**
 * GlobalView — StatusBadge Component
 *
 * Reusable badge for status display (§25, §26).
 * Variants: pending, active, suspended, rejected, online, offline, degraded.
 */

import styles from './StatusBadge.module.css';

export type BadgeVariant =
  | 'pending' | 'active' | 'suspended' | 'rejected'
  | 'online' | 'offline' | 'degraded'
  | 'info' | 'warning' | 'error';

interface StatusBadgeProps {
  variant: BadgeVariant;
  label?: string;
  pulse?: boolean;
  id?: string;
}

const VARIANT_MAP: Record<BadgeVariant, { color: string; defaultLabel: string }> = {
  pending:   { color: 'var(--accent5)', defaultLabel: 'PENDING' },
  active:    { color: 'var(--accent4)', defaultLabel: 'ACTIVE' },
  suspended: { color: 'var(--accent3)', defaultLabel: 'SUSPENDED' },
  rejected:  { color: 'var(--accent3)', defaultLabel: 'REJECTED' },
  online:    { color: 'var(--accent4)', defaultLabel: 'ONLINE' },
  offline:   { color: 'var(--text-muted)', defaultLabel: 'OFFLINE' },
  degraded:  { color: 'var(--accent5)', defaultLabel: 'DEGRADED' },
  info:      { color: 'var(--accent)',  defaultLabel: 'INFO' },
  warning:   { color: 'var(--accent5)', defaultLabel: 'WARNING' },
  error:     { color: 'var(--accent3)', defaultLabel: 'ERROR' },
};

export default function StatusBadge({ variant, label, pulse, id }: StatusBadgeProps) {
  const config = VARIANT_MAP[variant];
  return (
    <span
      className={`${styles.badge} ${pulse ? styles.pulse : ''}`}
      style={{ '--badge-color': config.color } as React.CSSProperties}
      id={id}
    >
      <span className={styles.dot} />
      {label || config.defaultLabel}
    </span>
  );
}
